/**
 * Tablet kanban board — a kitchen-display (KDS) layout for the Orders page.
 *
 * Renders the order lifecycle as four side-by-side, independently scrolling
 * columns (Cooking → Plating & QC → Ready → Delivery) with compact,
 * touch-sized cards. Designed for landscape tablets at the expo station.
 *
 * Reuses the real BoardOrder data and the same dismiss/pass handlers as the
 * grid and rows views — only the layout differs.
 */
import { useEffect, useState } from 'react';
import {
  Flame, Utensils, BellRing, Bike, CheckCircle2,
  Check, Bell, User, Navigation, MapPin, Loader2,
} from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { KpiStat } from '@/components/common/KpiStat';
import { elapsedPct, formatElapsed, useTicketElapsed } from '@/lib/elapsed';
import type { BoardOrder } from '@/lib/api/services/orders.api';
import {
  PrioPill, StatePill, TimerChip, ChefAvatar,
} from './OrderPrimitives';

// ─── Buckets passed in from the page ──────────────────────────────────────────

export interface KanbanBuckets {
  cooking: BoardOrder[];
  plating: BoardOrder[];
  pickup: BoardOrder[];
  delivery: BoardOrder[];
  completed: BoardOrder[];
}

interface KanbanHandlers {
  buckets: KanbanBuckets;
  onCookingComplete: (id: number) => void;
  onPlatingPass: (id: number) => void;
  onFulfilled: (id: number) => void;
  pendingCookingIds: Set<number>;
  pendingPlatingIds: Set<number>;
  pendingFulfilledIds: Set<number>;
  canUpdate: boolean;
}

// ─── "now" tick for waiting-time chips (mirrors useNow in orders.tsx) ──────────

function useNow(intervalMs = 30_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function minutesSince(iso: string | null, now: number): number | null {
  if (!iso) return null;
  return Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
}

// ─── Ticket number — gold "#" hero, matching the design ───────────────────────

function TicketNum({ id }: { id: number }) {
  return (
    <div className="inline-flex items-baseline gap-0.5 leading-none">
      <span className="text-[1.125rem] font-bold text-yellow-500">#</span>
      <span className="text-[1.5rem] font-extrabold tracking-tight text-(--color-text-primary)">
        {String(id).padStart(3, '0')}
      </span>
    </div>
  );
}

// ─── Progress tone — colour follows pace, matching the prototype's barColor ────

type Tone = 'destructive' | 'warn' | 'success' | 'foreground';

function progressTone(pct: number, isLate: boolean, isPaused: boolean): Tone {
  if (isLate) return 'destructive';
  if (isPaused) return 'warn';
  if (pct >= 95) return 'warn';
  if (pct >= 70) return 'success';
  return 'foreground';
}

const TONE_BG: Record<Tone, string> = {
  destructive: 'bg-(--color-danger)',
  warn: 'bg-(--color-warning)',
  success: 'bg-(--color-success)',
  foreground: 'bg-(--color-text-primary)',
};
const TONE_TEXT: Record<Tone, string> = {
  destructive: 'text-(--color-danger)',
  warn: 'text-(--color-warning)',
  success: 'text-(--color-success)',
  foreground: 'text-(--color-text-primary)',
};

// ─── Shared card shell ────────────────────────────────────────────────────────

function CardShell({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <article className={cn(
      'bg-(--color-surface-content) border border-(--color-border) rounded-[0.875rem] p-3 flex flex-col gap-2.5',
      'transition-transform active:scale-[0.99]',
      className,
    )}>
      {children}
    </article>
  );
}

/** 48px-tall action button for fat-finger tablet targets. */
function ActButton({ tone, pending, onClick, children }: {
  tone: string;
  pending?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      onClick={onClick}
      disabled={pending}
      className={cn('h-12 rounded-xl gap-1.5 font-bold text-[0.9375rem] disabled:opacity-70', tone)}
    >
      {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
    </Button>
  );
}

// ─── Cooking card ─────────────────────────────────────────────────────────────

function CookingKanbanCard({ order, onComplete, pending, canUpdate }: {
  order: BoardOrder;
  onComplete: (id: number) => void;
  pending: boolean;
  canUpdate: boolean;
}) {
  const tableLabel = order.table
    ? `${order.table.section} ${order.table.name}`
    : order.guestName ?? '—';
  const elapsedSeconds = useTicketElapsed(order);
  const pct = elapsedPct(elapsedSeconds, order.targetMinutes);
  const tone = progressTone(pct, order.isLate, order.isPaused);

  return (
    <CardShell>
      <div className="flex items-start justify-between gap-2">
        <div>
          <TicketNum id={order.id} />
          <div className="text-[0.8125rem] text-(--color-text-secondary) font-semibold mt-1">{tableLabel}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {order.isLate ? <StatePill state="late" /> : order.isPaused ? <StatePill state="paused" /> : null}
          <PrioPill prio={order.priority} />
        </div>
      </div>

      <div className="text-[0.9375rem] font-bold text-(--color-text-primary) leading-tight text-balance">{order.title}</div>

      <div>
        <div className="flex items-center justify-between text-[0.75rem] font-semibold mb-1">
          <TimerChip label={`${formatElapsed(elapsedSeconds)} / ${order.targetMinutes}m`} />
          <span className={TONE_TEXT[tone]}>{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-(--color-surface-accent) overflow-hidden">
          <div className={cn('h-full rounded-full', TONE_BG[tone])} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {order.chefName && (
        <div className="flex items-center gap-1.5 text-[0.8125rem] font-semibold text-(--color-text-primary) min-w-0">
          <ChefAvatar name={order.chefName} className="w-6 h-6" />
          <span className="truncate">{order.chefName}</span>
        </div>
      )}

      {canUpdate && (
        <ActButton
          tone="bg-(--color-success) hover:bg-(--color-success-hover) text-(--brand-white)"
          pending={pending}
          onClick={() => onComplete(order.id)}
        >
          <Check className="h-4 w-4" /><Trans>Complete</Trans>
        </ActButton>
      )}
    </CardShell>
  );
}

// ─── Plating card ─────────────────────────────────────────────────────────────

function PlatingKanbanCard({ order, onPass, pending, canUpdate }: {
  order: BoardOrder;
  onPass: (id: number) => void;
  pending: boolean;
  canUpdate: boolean;
}) {
  const { t } = useLingui();
  const tableLabel = order.table ? `${order.table.section} ${order.table.name}` : '—';

  return (
    <CardShell className="border-(--color-order-plating-bg)">
      <div className="flex items-start justify-between gap-2">
        <div>
          <TicketNum id={order.id} />
          <div className="text-[0.8125rem] text-(--color-text-secondary) font-semibold mt-1">{tableLabel}</div>
        </div>
        <PrioPill prio={order.priority} />
      </div>

      <div className="text-[0.9375rem] font-bold text-(--color-text-primary) leading-tight text-balance">{order.title}</div>

      <TimerChip icon={Utensils} label={t`On the pass`} />

      {order.chefName && (
        <div className="flex items-center gap-1.5 text-[0.8125rem] font-semibold text-(--color-text-primary) min-w-0">
          <ChefAvatar name={order.chefName} className="w-6 h-6" />
          <span className="truncate">{order.chefName}</span>
        </div>
      )}

      {canUpdate && (
        <ActButton
          tone="bg-(--color-order-plating-fg) hover:bg-(--color-order-plating-fg)/90 text-(--brand-white)"
          pending={pending}
          onClick={() => onPass(order.id)}
        >
          <Bell className="h-4 w-4" /><Trans>Mark Ready</Trans>
        </ActButton>
      )}
    </CardShell>
  );
}

// ─── Pickup card ──────────────────────────────────────────────────────────────

function PickupKanbanCard({ order, onHandoff, pending, canUpdate, now }: {
  order: BoardOrder;
  onHandoff: (id: number) => void;
  pending: boolean;
  canUpdate: boolean;
  now: number;
}) {
  const { t } = useLingui();
  const waited = minutesSince(order.passedAt ?? order.sentAt, now);

  return (
    <CardShell>
      <div className="flex items-start justify-between gap-2">
        <div>
          <TicketNum id={order.id} />
          <div className="text-[0.8125rem] text-(--color-text-secondary) font-semibold mt-1"><Trans>Counter pickup</Trans></div>
        </div>
        <PrioPill prio={order.priority} />
      </div>

      <div className="text-[0.9375rem] font-bold text-(--color-text-primary) leading-tight text-balance">{order.title}</div>

      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[0.8125rem] font-semibold text-(--color-text-primary) min-w-0">
          <User className="w-4 h-4 text-(--color-text-secondary) shrink-0" />
          <span className="truncate">{order.guestName ?? t`Walk-in`}</span>
        </span>
        {waited != null && <TimerChip label={`${waited}m`} />}
      </div>

      {canUpdate && (
        <ActButton
          tone="bg-(--brand-blue) hover:bg-(--brand-blue)/90 text-(--brand-white)"
          pending={pending}
          onClick={() => onHandoff(order.id)}
        >
          <CheckCircle2 className="h-4 w-4" /><Trans>Hand off</Trans>
        </ActButton>
      )}
    </CardShell>
  );
}

// ─── Delivery card ────────────────────────────────────────────────────────────

function DeliveryKanbanCard({ order, onDelivered, pending, canUpdate, now }: {
  order: BoardOrder;
  onDelivered: (id: number) => void;
  pending: boolean;
  canUpdate: boolean;
  now: number;
}) {
  const { t } = useLingui();
  const enrouteFor = minutesSince(order.passedAt ?? order.sentAt, now);

  return (
    <CardShell>
      <div className="flex items-start justify-between gap-2">
        <div>
          <TicketNum id={order.id} />
          <div className="text-[0.8125rem] text-(--color-text-secondary) font-semibold mt-1 truncate max-w-[10rem]">
            {order.guestName ?? t`Delivery`}
          </div>
        </div>
        <PrioPill prio={order.priority} />
      </div>

      <div className="text-[0.9375rem] font-bold text-(--color-text-primary) leading-tight text-balance">{order.title}</div>

      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[0.8125rem] font-semibold text-(--color-text-primary) min-w-0">
          {order.chefName ? (
            <>
              <ChefAvatar name={order.chefName} className="w-6 h-6" />
              <span className="truncate">{order.chefName}</span>
            </>
          ) : (
            <><Navigation className="w-4 h-4 text-(--color-text-secondary) shrink-0" /><Trans>Courier</Trans></>
          )}
        </span>
        {enrouteFor != null && <TimerChip icon={MapPin} label={t`${enrouteFor}m out`} />}
      </div>

      {canUpdate && (
        <ActButton
          tone="bg-(--color-text-primary) hover:bg-(--color-text-primary)/90 text-(--color-surface-page)"
          pending={pending}
          onClick={() => onDelivered(order.id)}
        >
          <CheckCircle2 className="h-4 w-4" /><Trans>Delivered</Trans>
        </ActButton>
      )}
    </CardShell>
  );
}

// ─── Column ───────────────────────────────────────────────────────────────────

function Column({ dot, title, count, countClass, emptyIcon: EmptyIcon, emptyLabel, children }: {
  dot: string;
  title: React.ReactNode;
  count: number;
  countClass: string;
  emptyIcon: React.ComponentType<{ className?: string }>;
  emptyLabel: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="flex-1 min-w-0 flex flex-col bg-(--color-surface-muted) border border-(--color-border) rounded-[1.125rem] overflow-hidden">
      <div className="px-3.5 py-3 flex items-center gap-2 border-b border-(--color-border) bg-(--color-surface-content) shrink-0">
        <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', dot)} />
        <span className="text-[0.9375rem] font-extrabold tracking-tight text-(--color-text-primary)">{title}</span>
        <span className={cn('ml-auto text-[0.75rem] font-extrabold px-2 py-0.5 rounded-full', countClass)}>{count}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2.5">
        {count === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 py-10 text-(--color-text-secondary)">
            <EmptyIcon className="h-7 w-7 opacity-30" />
            <p className="text-[0.8125rem] text-center">{emptyLabel}</p>
          </div>
        ) : children}
      </div>
    </section>
  );
}

// ─── KPI chip row ─────────────────────────────────────────────────────────────

function KpiChip({ icon, iconClass, count, label }: {
  icon: React.ComponentType<{ className?: string }>;
  iconClass: string;
  count: number;
  label: React.ReactNode;
}) {
  return (
    <KpiStat size="compact" icon={icon} iconClassName={iconClass} count={count} label={label} />
  );
}

export function KanbanKpiRow({ buckets }: { buckets: KanbanBuckets }) {
  return (
    <div className="flex items-center gap-2.5 overflow-x-auto pb-1 shrink-0">
      <KpiChip icon={Flame} iconClass="bg-(--color-order-cooking-bg) text-(--color-order-cooking-fg)" count={buckets.cooking.length} label={<Trans>Cooking</Trans>} />
      <KpiChip icon={Utensils} iconClass="bg-(--color-order-plating-bg) text-(--color-order-plating-fg)" count={buckets.plating.length} label={<Trans>Plating</Trans>} />
      <KpiChip icon={BellRing} iconClass="bg-(--color-order-pickup-bg) text-(--color-order-pickup-fg)" count={buckets.pickup.length} label={<Trans>Ready</Trans>} />
      <KpiChip icon={Bike} iconClass="bg-(--color-order-cooking-bg) text-(--color-order-cooking-fg)" count={buckets.delivery.length} label={<Trans>Delivery</Trans>} />
      <KpiChip icon={CheckCircle2} iconClass="bg-(--color-order-completed-bg) text-(--color-order-completed-fg)" count={buckets.completed.length} label={<Trans>Done today</Trans>} />
    </div>
  );
}

// ─── Board ────────────────────────────────────────────────────────────────────

export function OrdersKanban({
  buckets,
  onCookingComplete, onPlatingPass, onFulfilled,
  pendingCookingIds, pendingPlatingIds, pendingFulfilledIds,
  canUpdate,
}: KanbanHandlers) {
  const now = useNow();

  return (
    <div className="h-full flex gap-3">
      <Column
        dot="bg-(--color-warning)"
        title={<Trans>Cooking</Trans>}
        count={buckets.cooking.length}
        countClass="bg-(--color-order-cooking-bg) text-(--color-order-cooking-fg)"
        emptyIcon={Flame}
        emptyLabel={<Trans>No active tickets on the fire.</Trans>}
      >
        {buckets.cooking.map(o => (
          <CookingKanbanCard key={o.id} order={o} onComplete={onCookingComplete} pending={pendingCookingIds.has(o.id)} canUpdate={canUpdate} />
        ))}
      </Column>

      <Column
        dot="bg-(--color-order-plating-fg)"
        title={<Trans>Plating & QC</Trans>}
        count={buckets.plating.length}
        countClass="bg-(--color-order-plating-bg) text-(--color-order-plating-fg)"
        emptyIcon={Utensils}
        emptyLabel={<Trans>The pass is clear.</Trans>}
      >
        {buckets.plating.map(o => (
          <PlatingKanbanCard key={o.id} order={o} onPass={onPlatingPass} pending={pendingPlatingIds.has(o.id)} canUpdate={canUpdate} />
        ))}
      </Column>

      <Column
        dot="bg-(--brand-blue)"
        title={<Trans>Ready</Trans>}
        count={buckets.pickup.length}
        countClass="bg-(--color-order-pickup-bg) text-(--color-order-pickup-fg)"
        emptyIcon={BellRing}
        emptyLabel={<Trans>Counter is clear.</Trans>}
      >
        {buckets.pickup.map(o => (
          <PickupKanbanCard key={o.id} order={o} onHandoff={onFulfilled} pending={pendingFulfilledIds.has(o.id)} canUpdate={canUpdate} now={now} />
        ))}
      </Column>

      <Column
        dot="bg-(--color-warning)"
        title={<Trans>Delivery</Trans>}
        count={buckets.delivery.length}
        countClass="bg-(--color-order-cooking-bg) text-(--color-order-cooking-fg)"
        emptyIcon={Bike}
        emptyLabel={<Trans>No active deliveries.</Trans>}
      >
        {buckets.delivery.map(o => (
          <DeliveryKanbanCard key={o.id} order={o} onDelivered={onFulfilled} pending={pendingFulfilledIds.has(o.id)} canUpdate={canUpdate} now={now} />
        ))}
      </Column>
    </div>
  );
}
